import React, { useEffect, useMemo, useState } from 'react';
import DefaultMediaFallback from '../../components/common/DefaultMediaFallback';
import { mediaStrings, moduleCommonStrings } from '../../content/hobbyModules';

/**
 * Retrieves search error message.
 *
 * @param {*} error - Failure being normalized or reported.
 * @returns {*} The derived operation result.
 */
function getSearchErrorMessage(error) {
  const message = error?.message || '';
  if (message.includes('BOOK_SEARCH_RATE_LIMITED')) {
    return mediaStrings.reading.addSearch.errors.rateLimited;
  }
  if (message.includes('BOOK_SEARCH_UNAVAILABLE')) {
    return mediaStrings.reading.addSearch.errors.unavailable;
  }
  return message;
}

/**
 * Renders the reading add search experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.results - results value.
 * @param {*} props.onCancel - Callback invoked when cancel occurs.
 * @param {*} props.onManualEntry - Callback invoked when manual entry occurs.
 * @param {*} props.onSearch - Callback invoked when search occurs.
 * @param {*} props.onUseBook - Callback invoked when use book occurs.
 * @param {*} props.isSearching - Whether is searching is enabled.
 * @param {*} props.error - Failure being normalized or reported.
 * @returns {JSX.Element} The rendered interface.
 */
export default function ReadingAddSearch({ results, onCancel, onManualEntry, onSearch, onUseBook, isSearching, error }) {
  const [query, setQuery] = useState('');
  const trimmedQuery = query.trim();
  const canSearch = trimmedQuery.length >= 2;

  useEffect(() => {
    if (!canSearch) return undefined;
    const timer = window.setTimeout(() => onSearch(trimmedQuery), 250);
    return () => window.clearTimeout(timer);
  }, [canSearch, onSearch, trimmedQuery]);

  const visibleResults = useMemo(() => (canSearch && !isSearching ? results : []), [canSearch, isSearching, results]);

  return (
    <div className="movie-add-search">
      <button type="button" className="recipe-back-link" onClick={onCancel}>← Back</button>

      <section className="recipe-form-section priority-section">
        <div className="form-group">
          <label>{mediaStrings.reading.addSearch.label}</label>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={mediaStrings.reading.addSearch.placeholder}
            autoFocus
          />
        </div>

        {isSearching && <p className="muted-copy movie-search-status">{moduleCommonStrings.actions.searching}</p>}
        {error && <p className="error-copy">Error: {getSearchErrorMessage(error)}</p>}

        <div className="movie-import-results movie-add-results">
          {visibleResults.map((book) => (
            <button type="button" className="movie-import-result" key={book.catalogId} onClick={() => onUseBook(book)}>
              {book.coverUrl ? (
                <img src={book.coverUrl} alt="" />
              ) : (
                <DefaultMediaFallback className="movie-poster-placeholder" fallbackType="reading" />
              )}
              <div>
                <h3>{book.title}</h3>
                <p>{[book.authors.join(', '), book.publishedYear, book.pageCount && `${book.pageCount} pages`].filter(Boolean).join(' - ')}</p>
                <p>{book.description}</p>
                <div className="recipe-tags">
                  {book.genres.map((genre) => <span key={genre}>{genre}</span>)}
                </div>
              </div>
            </button>
          ))}
        </div>

        {canSearch && !isSearching && visibleResults.length === 0 && (
          <div className="empty-state movie-import-empty">
            <p>{mediaStrings.reading.addSearch.empty}</p>
            <button type="button" className="btn-secondary" onClick={onManualEntry}>{moduleCommonStrings.actions.addManually}</button>
          </div>
        )}

      </section>
    </div>
  );
}
